import type { Rule } from 'antd/es/form';
import { Decimal } from 'decimal.js';
import { match } from 'ts-pattern';

export function buildNumericInputRules(
    options: { isRequired?: boolean; customRules?: Rule[] } & (
        | {
              valueType: 'decimal';
              min?: Decimal;
              max?: Decimal;
          }
        | {
              valueType: 'bigint';
              min?: bigint;
              max?: bigint;
          }
        | {
              valueType: 'number';
              min?: number;
              max?: number;
          }
    ),
): Rule[] {
    return [
        {
            // Only for asterisk on UI, actual validation for isRequired happens in validator below
            ...(options?.isRequired ? { required: true } : {}),
            async validator(_, value) {
                // `null` signals an invalid input (e.g. unparseable text from NumericInput) and
                // is always rejected as malformed. `undefined` means the field is empty: rejected
                // with a "required" message when isRequired, otherwise left valid.
                if (value === null) {
                    throw new Error('Invalid number');
                }

                if (
                    options?.isRequired &&
                    match(options.valueType)
                        .with('decimal', () => !Decimal.isDecimal(value))
                        .with('bigint', () => typeof value !== 'bigint')
                        .with('number', () => typeof value !== 'number')
                        .exhaustive()
                ) {
                    throw new Error('Field is required');
                }
            },
        },
        ...(options?.min !== undefined
            ? ([
                  {
                      async validator(_, value: Decimal | bigint | number) {
                          if (value === null || value === undefined || options.min === undefined) {
                              return;
                          }

                          if (
                              match(options.valueType)
                                  .with('decimal', () => (value as Decimal).lt(options.min as Decimal))
                                  .with('bigint', () => (value as bigint) < (options.min as bigint))
                                  .with('number', () => (value as number) < (options.min as number))
                                  .exhaustive()
                          ) {
                              throw new Error(`Field must be ${options.min.toString()} or greater`);
                          }
                      },
                  },
              ] satisfies Rule[])
            : []),
        ...(options?.max !== undefined
            ? ([
                  {
                      async validator(_, value: Decimal | bigint | number) {
                          if (value === null || value === undefined || options.max === undefined) {
                              return;
                          }

                          if (
                              match(options.valueType)
                                  .with('decimal', () => (value as Decimal).gt(options.max as Decimal))
                                  .with('bigint', () => (value as bigint) > (options.max as bigint))
                                  .with('number', () => (value as number) > (options.max as number))
                                  .exhaustive()
                          ) {
                              throw new Error(`Field must be ${options.max.toString()} or less`);
                          }
                      },
                  },
              ] satisfies Rule[])
            : []),
        ...(options?.customRules ?? []),
    ];
}
